import { getSessionId } from "@redux/memoizedSelectors";
import { handleAddRenderElement } from "@utils/helpers";
import { getterApis } from "@utils/optionsSets/getterApis";
import ActionListCard from "features/analytics/Components/Cards/ActionListCard";
import MapListCard from "features/analytics/Components/Cards/MapListCard";
import OpportunityListCard from "features/analytics/Components/Cards/OpportunityListCard";
import SolutionListCard from "features/analytics/Components/Cards/SolutionListCard";
import CustomWordCloud from "features/analytics/Components/CustomWordCloud";
import Export from "features/analytics/Components/Export";
import FiltersManager from "features/analytics/Components/Filters/FiltersManager";
import HorizontalBarsChart from "features/analytics/Components/HorizontalBarsChart";
import { InsightTypeCard } from "features/analytics/Components/InsightTypeCard";
import Section from "features/analytics/Components/Section";
import { AnalyticsLogoHeader, Body, BodySection, DashboardContainer, TopSection } from "features/analytics/Style";
import { applyFilterJourneyTrend } from "features/analytics/reducer/JourneyTrend/journeyTrendActions";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Counters from "./Reports/Counters";
import MapsPerStatus from "./Reports/MapsPerStatus";
import OpportunityAnalysisMatrix from "./Reports/OpportunityAnalysisMatrix";
import QuadrantScatterChart from "./Reports/QuadrantScatterChart";
import TopNTouchpoints from "./Reports/TopNTouchpoints";
import TopTenWithLabels from "./Reports/TopTenWithLabels";
import { entitiesByJourneyDataSelector, insightsHorizontalBarDataSelector, topFiveDataSelector, wordCloudDataSelector } from "./journeyTrendDataSelectors";
import React from "react";

function JourneyTrendsReports() {
	const { t } = useTranslation();
	const pdfRef = React.useRef();
	const hideForPdf = useLocation().search.hideForPdf();
	const sessionId = useSelector(getSessionId);
	const logoRefreshValue = useSelector((state) => state.settings.organization?.logoRefreshValue);
	const logoUrl = getterApis["GET_ACCOUNT_IMG"](sessionId, logoRefreshValue);

	const getFiltersToSave = ({ currentEntityData }) => {
		return {
			programs: currentEntityData?.selectedFilterOptions?.programs?.map((p) => p?.id),
			cjms: currentEntityData?.selectedFilterOptions?.cjms?.map((j) => j?.id),
			personas: currentEntityData?.selectedFilterOptions?.personas?.map((p) => p?.id),
		};
	};

	return (
		<DashboardContainer>
			{hideForPdf && <AnalyticsLogoHeader src={logoUrl} alt="logo" loading="lazy" />}
			<TopSection>
				<FiltersManager filterEntities={["programs", "cjms", "personas"]} applyAction={applyFilterJourneyTrend} dashboardName="journeyTrend" />
				<Export dashboardName="journeytrends" fileName={t("JOURNEY_TRENDS")} getFiltersToSave={getFiltersToSave} pdfRef={pdfRef} />
			</TopSection>
			<BodySection ispdf={hideForPdf}>
				<Body ref={pdfRef}>
					<Counters />
					<Section>
						<TopNTouchpoints title={t("TOP_TEN_TOUCHPOINTS")} growFactor={2} />
						<MapsPerStatus title={t("MAPS_PER_STATUS")} />
					</Section>
					<Section height="200">
						<InsightTypeCard title={t("GAIN_POINTS")} background="linear-gradient(2deg, #FFF 6.47%, #F2FFF8 95.32%)" border="#C7FFDB" gemColor="#22C55E" type="gain" />
						<InsightTypeCard title={t("PAIN_POINTS")} background="linear-gradient(2deg, #FFF 6.47%, #FFF0F0 95.32%)" border="#FFC7C7" gemColor="#FF4D4F" type="pain" />
					</Section>
					<Section>
						<HorizontalBarsChart
							title={t("TOP_FIVE_GAIN_POINTS")}
							dataSelector={(state) => insightsHorizontalBarDataSelector(state, { type: "gain" })}
							barColor="#BBF7D0"
							relatedItemsTitle={t("ASPX_97")}
							listItemComponent={MapListCard}
							itemAttributeId="cjmId"
							emptyDataText="TOP_GAIN_REPORT_DESCRIPTION"
						/>
						<HorizontalBarsChart
							title={t("TOP_FIVE_PAIN_POINTS")}
							dataSelector={(state) => insightsHorizontalBarDataSelector(state, { type: "pain" })}
							barColor="#FCA5A5"
							relatedItemsTitle={t("ASPX_97")}
							listItemComponent={MapListCard}
							itemAttributeId="cjmId"
							emptyDataText="TOP_PAIN_REPORT_DESCRIPTION"
						/>
					</Section>
					<Section>
						<CustomWordCloud
							title={t("GAIN_POINTS_WORD_CLOUD")}
							dataSelector={(state) => wordCloudDataSelector(state, { type: "gain" })}
							colorPalette={["#16A34A", "#15803D", "#166534"]}
							emptyDataText="GAIN_WORD_CLOUD_REPORT_DESCRIPTION"
						/>
						<CustomWordCloud
							title={t("PAIN_POINTS_WORD_CLOUD")}
							dataSelector={(state) => wordCloudDataSelector(state, { type: "pain" })}
							colorPalette={["#DC2626", "#B91C1C", "#7F1D1D"]}
							emptyDataText="PAIN_WORD_CLOUD_REPORT_DESCRIPTION"
						/>
					</Section>

					<Section>
						<TopTenWithLabels entity="tag" title={t("TOP_TEN_TAGS")} barFill="#605F60" emptyDataText="TOP_TAGS_REPORT_DESCRIPTION" />
						<TopTenWithLabels entity="group" title={t("TOP_TEN_GROUPS")} barFill="#18AEAD" emptyDataText="TOP_GROUPS_REPORT_DESCRIPTION" />
					</Section>
					<Section>
						<HorizontalBarsChart
							title={t("TOP_FIVE_OPPORTUNITIES")}
							dataSelector={(state) => topFiveDataSelector(state, { entity: "opportunity" })}
							barColor="#5EEAD4"
							relatedItemsTitle={t("ASPX_97")}
							listItemComponent={MapListCard}
							emptyDataText="OPPORTUNITY_REPORT_DESCRIPTION"
						/>
						<HorizontalBarsChart
							title={t("TOP_FIVE_SOLUTIONS")}
							dataSelector={(state) => topFiveDataSelector(state, { entity: "solution" })}
							barColor="#D8B4FE"
							relatedItemsTitle={t("ASPX_97")}
							listItemComponent={MapListCard}
							emptyDataText="SOLUTION_REPORT_DESCRIPTION"
						/>
					</Section>
					<Section>
						<OpportunityAnalysisMatrix title={t("OPPORTUNITY_ANALYSIS_MATRIX")} />
					</Section>
					<Section>
						<QuadrantScatterChart title={t("SOLUTION_ANALYSIS_MATRIX")} />
					</Section>
					<Section>
						<HorizontalBarsChart
							title={`${t("OPPORTUNITIES_BY_JOURNEY")} (${t("TOP_FIVE")})`}
							dataSelector={(state) => entitiesByJourneyDataSelector(state, { entity: "opportunity" })}
							barColor="#5EEAD4"
							relatedItemsTitle={t("OPPORTUNITIES")}
							listItemComponent={OpportunityListCard}
							emptyDataText="ITEMS_BY_JOURNEY_REPORT_DESCRIPTION"
							backdrop={false}
						/>
						<HorizontalBarsChart
							title={`${t("SOLUTIONS_BY_JOURNEY")} (${t("TOP_FIVE")})`}
							dataSelector={(state) => entitiesByJourneyDataSelector(state, { entity: "solution" })}
							barColor="#D8B4FE"
							relatedItemsTitle={t("SOLUTIONS")}
							listItemComponent={SolutionListCard}
							emptyDataText="ITEMS_BY_JOURNEY_REPORT_DESCRIPTION"
							backdrop={false}
						/>
						<HorizontalBarsChart
							title={`${t("ACTIONS_BY_JOURNEY")} (${t("TOP_FIVE")})`}
							dataSelector={(state) => entitiesByJourneyDataSelector(state, { entity: "action" })}
							barColor="#93C5FD"
							relatedItemsTitle={t("JS_80")}
							listItemComponent={ActionListCard}
							emptyDataText="ITEMS_BY_JOURNEY_REPORT_DESCRIPTION"
							backdrop={false}
						/>
					</Section>
				</Body>
			</BodySection>
			{hideForPdf && <div id="addRenderElement">{handleAddRenderElement(3000)} </div>}
		</DashboardContainer>
	);
}

JourneyTrendsReports.displayName = "JourneyTrendsReports";
export default JourneyTrendsReports;
